import * as React from 'react';
import {Link as RoutedLink} from 'react-router-dom';

import {Nut_Settings_User} from 'nutshell-core/settings-constants';

import {Button} from 'shells/button/button';
import {colors} from 'shells/colors/colors';
import {ZoomIcon, GoogleMeetIcon, MicrosoftTeamsIcon, VirtualMeetingIcon} from 'shells/icon';
import {StackLegacy} from 'shells/layout/stack/stack-legacy';
import {Link} from 'shells/link/link';
import type {MessageType} from 'shells/message/message';
import {ConnectionInfoBody} from 'shells/status-button/connection-info-body';
import {ConnectionInfoTitle} from 'shells/status-button/connection-info-title';
import {Body} from 'shells/typography/body';

import type {ExternalServiceType} from 'nutshell-graphql-types';
import type {
    SetZoomIsEnabledInput,
    SetGoogleMeetIsEnabledInput,
    SetMicrosoftTeamsIsEnabledInput,
    GetSessionUser_loggedInUser as User,
} from 'nutshell-graphql-types';

import {ZoomFeatureGate} from '../limits/feature-gates/zoom-feature-gate';
import type {MonthlyUsage} from '../limits/get-usage-from-limits';
import {authToService} from '../setup/integrations/auth-to-service';

import {
    LOG_MODE_AUTOLOG,
    LOG_MODE_MANUAL,
    USER_SETTING_HAS_ENABLED_GOOGLE_MEET_AUTOLOG,
    USER_SETTING_HAS_ENABLED_ZOOM_AUTOLOG,
    USER_SETTING_HAS_ENABLED_MICROSOFT_TEAMS_AUTOLOG,
    USER_SETTING_HAS_ENABLED_AIRCALL_AUTOLOG,
    USER_SETTING_HAS_ENABLED_CALLRAIL_AUTOLOG,
    USER_SETTING_HAS_ENABLED_RINGCENTRAL_AUTOLOG,
    USER_SETTING_HAS_ENABLED_DIALPAD_AUTOLOG,
    USER_SETTING_HAS_ENABLED_KIXIE_AUTOLOG,
    TAB_UNLOGGED,
    TAB_LOGGED,
} from './tables/consts';
import type {LogMode, ActiveTab} from './tables/consts';
import type {ExternalServiceTypeFrontend} from './tables/types';

export const translateApiTypeForLinkedEntity = (typename: string) => {
    switch (typename) {
        case 'Account':
            return 'accounts';
        case 'Contact':
            return 'contacts';
        case 'Lead':
            return 'leads';
        case 'Origin':
            return 'origins';
        case 'User':
            return 'users';
        default:
            return 'contacts';
    }
};

export const getProcessStatusText = (status: string) => {
    switch (status) {
        case 'PROCESSED':
            return 'Processed';
        case 'PROCESSING':
            return 'Processing…';
        case 'STARTED':
            return 'Started';
        case 'NONE':
            return 'None';
    }
};

const WINDOW_FEATURES = {
    toolbar: 'no',
    location: 'no',
    width: 600,
    height: 850,
} as const;

export function getFlashBannerType(isEnabled: boolean, errorMessage?: string | null): MessageType {
    if (errorMessage) {
        return 'warning';
    } else if (isEnabled) {
        return 'success';
    }

    return 'info';
}

function isGoogleMeetOrMicrosoftTeams(serviceType: ExternalServiceType): boolean {
    return serviceType === 'GOOGLE_MEET' || serviceType === 'MICROSOFT_TEAMS';
}

export function getStatusText(
    isEnabled: boolean,
    errorMessage: string | null | undefined,
    serviceType: ExternalServiceType
): string {
    const noun = getServiceNoun(serviceType);

    if (errorMessage) {
        return `${noun}: Connection problems`;
    }

    if (isEnabled) {
        switch (serviceType) {
            case 'ZOOM':
                return `${noun}: Connected`;
            case 'GOOGLE_MEET':
            case 'MICROSOFT_TEAMS':
                return `${noun}: Enabled`;
            default:
                return `${noun}: Not yet connected`;
        }
    }

    if (isGoogleMeetOrMicrosoftTeams(serviceType)) {
        return `${noun}: Disabled`;
    }

    return `${noun}: Not yet connected`;
}

export function getStatusDescription(
    isEnabled: boolean,
    errorMessage: string | null | undefined,
    serviceType: ExternalServiceType
): React.ReactElement<any> {
    const noun = getServiceNoun(serviceType);

    if (errorMessage) {
        return <div>Your {noun} account is having connection problems</div>;
    }

    if (isEnabled) {
        switch (serviceType) {
            case 'ZOOM':
                return <div>Your {noun} account is connected</div>;
            case 'GOOGLE_MEET':
            case 'MICROSOFT_TEAMS':
                return <div>Your {noun} integration is enabled</div>;
            default:
                return <div>Your {noun} account is not yet connected</div>;
        }
    }

    if (isGoogleMeetOrMicrosoftTeams(serviceType)) {
        return <div>Your {noun} integration is disabled</div>;
    }

    return <div>Your {noun} account is not yet connected</div>;
}

export function getFeatureGate(
    serviceType: ExternalServiceType
): React.ReactElement<any> | null | undefined {
    switch (serviceType) {
        case 'ZOOM':
            if (!NutClientConfig.hasZoomTranscription) {
                return <ZoomFeatureGate />;
            }

            return null;
        case 'GOOGLE_MEET':
            if (!NutClientConfig.hasGmeetTranscription) {
                // TODO: Add Google Meet feature gate
                return <ZoomFeatureGate />;
            }

            return null;
        case 'MICROSOFT_TEAMS':
            if (!NutClientConfig.hasMicrosoftTeamsTranscription) {
                // TODO: Add Microsoft Teams feature gate
                return <ZoomFeatureGate />;
            }

            return null;
        default:
            return null;
    }
}

export function getPopoverContent(input: {
    isEnabled: boolean;
    errorMessage: string | null | undefined;
    userEmail: string | null | undefined;
    setIsDialogOpen: (isOpen: boolean) => void;
    handleReconfigure: () => void;
    serviceType: ExternalServiceType;
}): React.ReactElement<any> {
    const {isEnabled, errorMessage, userEmail, setIsDialogOpen, handleReconfigure, serviceType} =
        input;

    if (errorMessage) {
        return (
            <div className='pad-24'>
                <StackLegacy spacing={16} alignCenter={true}>
                    Meetings will not begin syncing into Nutshell again until you‘ve reconnected
                    your account.
                    <Button
                        variant='primary'
                        size='big'
                        onClick={() => {
                            handleReconfigure();
                            setIsDialogOpen(false);
                        }}
                    >
                        Reconnect
                    </Button>
                </StackLegacy>
            </div>
        );
    } else if (isEnabled) {
        const actionButton =
            serviceType === 'ZOOM' || serviceType === 'MICROSOFT_TEAMS' ? (
                <Button
                    variant='text-primary'
                    size='small'
                    onClick={() => {
                        handleReconfigure();
                        setIsDialogOpen(false);
                    }}
                >
                    Disconnect this account
                </Button>
            ) : (
                <Link
                    // @ts-expect-error
                    as={RoutedLink}
                    size='small'
                    variant='primary'
                    to={`${getServiceUrl(serviceType)}/settings`}
                    onClick={() => {
                        setIsDialogOpen(false);
                    }}
                >
                    Manage your {getServiceNoun(serviceType)} connection
                </Link>
            );

        return (
            <ConnectionInfoBody>
                <ConnectionInfoTitle
                    icon={getServiceTypeIcon(serviceType)}
                    primaryText={userEmail || ''}
                />
                <div className='flex justify-center'>{actionButton}</div>
            </ConnectionInfoBody>
        );
    }

    return (
        <ConnectionInfoBody>
            <ConnectionInfoTitle
                icon={getServiceTypeIcon(serviceType)}
                primaryText={userEmail || ''}
            />
            <div className='text-align-center'>
                <Link
                    // @ts-expect-error
                    as={RoutedLink}
                    size='small'
                    variant='primary'
                    to={`${getServiceUrl(serviceType)}/settings`}
                    onClick={() => {
                        setIsDialogOpen(false);
                    }}
                >
                    Manage your {getServiceNoun(serviceType)} connection
                </Link>
            </div>
        </ConnectionInfoBody>
    );
}

export function handleEnableZoom(props: {
    setZoomIsEnabled: (arg1: SetZoomIsEnabledInput) => Promise<any>;
    refetch: () => Promise<any>;
    setError: (error?: string | null | undefined) => void;
    onAuthStart?: () => void;
    onAuthSuccess?: () => void;
}) {
    return props.setZoomIsEnabled({isEnabled: true}).then((mutationResults) => {
        const authUrl =
            mutationResults && mutationResults.data && mutationResults.data.setZoomIsEnabled
                ? mutationResults.data.setZoomIsEnabled.authUrl
                : null;

        if (authUrl) {
            if (props.onAuthStart) {
                props.onAuthStart();
            }

            return authToService({
                authUrl,
                windowFeatures: WINDOW_FEATURES,
                authCheckUrl: '/zoom/has-auth',
            })
                .then((authInfo) => {
                    if (authInfo.hasAuth) {
                        if (props.onAuthSuccess) {
                            props.onAuthSuccess();
                            props.setError(null);
                        }
                        props.refetch();

                        return true;
                    }
                    // If hasAuth is false, the user likely canceled
                    props.setZoomIsEnabled({isEnabled: false});
                    props.refetch();

                    props.setError('Authentication failed');

                    return false;
                })
                .catch((errors) => {
                    props.setError(errors && errors.length ? errors[0] : 'Authentication failed');

                    return false;
                });
        }

        return false;
    });
}

export function handleEnableGoogleIntegration(props: {
    setGoogleMeetIsEnabled: (arg1: SetGoogleMeetIsEnabledInput) => Promise<any>;
    refetch: () => Promise<any>;
    setError: (error?: string | null | undefined) => void;
    onAuthStart?: () => void;
    onAuthSuccess?: () => void;
}): Promise<any> {
    return props
        .setGoogleMeetIsEnabled({isEnabled: true, meetAuthType: 'MEETINGS'})
        .then((mutationResults) => {
            const authUrl =
                mutationResults &&
                mutationResults.data &&
                mutationResults.data.setGoogleMeetIsEnabled
                    ? mutationResults.data.setGoogleMeetIsEnabled.authUrl
                    : null;

            if (authUrl) {
                if (props.onAuthStart) {
                    props.onAuthStart();
                }

                return authToService({
                    authUrl,
                    windowFeatures: WINDOW_FEATURES,
                    authCheckUrl: '/google/has-auth',
                })
                    .then((authInfo) => {
                        if (authInfo.hasAuth) {
                            if (props.onAuthSuccess) {
                                props.onAuthSuccess();
                                props.setError(null);
                            }
                            props.refetch();

                            return true;
                        }
                        // If hasAuth is false, the user likely canceled
                        props.setGoogleMeetIsEnabled({
                            isEnabled: false,
                            meetAuthType: 'MEETINGS',
                        });
                        props.refetch();

                        props.setError('Authentication failed');

                        return false;
                    })
                    .catch((errors) => {
                        props.setError(
                            errors && errors.length ? errors[0] : 'Authentication failed'
                        );

                        return false;
                    });
            }

            return false;
        });
}

export function handleEnableTeamsIntegration(props: {
    setMicrosoftTeamsIsEnabled: (arg1: SetMicrosoftTeamsIsEnabledInput) => Promise<any>;
    refetch: () => Promise<any>;
    setError: (error?: string | null | undefined) => void;
    onAuthStart?: () => void;
    onAuthSuccess?: () => void;
}): Promise<any> {
    return props.setMicrosoftTeamsIsEnabled({isEnabled: true}).then((mutationResults) => {
        const authUrl =
            mutationResults &&
            mutationResults.data &&
            mutationResults.data.setMicrosoftTeamsIsEnabled
                ? mutationResults.data.setMicrosoftTeamsIsEnabled.authUrl
                : null;

        if (authUrl) {
            if (props.onAuthStart) {
                props.onAuthStart();
            }

            authToService({
                authUrl,
                windowFeatures: WINDOW_FEATURES,
                authCheckUrl: '/office-365/has-auth?apiVersion=2',
            })
                .then(({hasAuth}) => {
                    if (hasAuth) {
                        if (props.onAuthSuccess) {
                            props.onAuthSuccess();
                        }
                        props.refetch();
                    }
                })
                .catch((errors) => props.setError(errors[0]));
        }
    });
}

export const getServiceTypeIcon = (serviceType: ExternalServiceType): React.ReactElement<any> => {
    switch (serviceType) {
        case 'ZOOM':
            return <ZoomIcon wrapWithDiv={false} size={24} />;
        case 'GOOGLE_MEET':
            return <GoogleMeetIcon wrapWithDiv={false} size={24} />;
        case 'MICROSOFT_TEAMS':
            return <MicrosoftTeamsIcon wrapWithDiv={false} size={24} />;
        default:
            return <VirtualMeetingIcon wrapWithDiv={false} size={24} />;
    }
};

export const getServiceTypeColor = (serviceType: ExternalServiceType): string => {
    switch (serviceType) {
        case 'ZOOM':
            return colors.zoomBlue;
        case 'GOOGLE_MEET':
            return colors.googleGreen;
        case 'MICROSOFT_TEAMS':
            return colors.teamsBlue;
        default:
            return colors.focusBlue;
    }
};
export const getServiceNoun = (
    serviceType: ExternalServiceType | ExternalServiceTypeFrontend | 'KIXIE' | 'SKYPE'
): string => {
    switch (serviceType) {
        case 'ZOOM':
            return 'Zoom';
        case 'GOOGLE_MEET':
            return 'Google Meet';
        case 'MICROSOFT_TEAMS':
            return 'Microsoft Teams';
        case 'PHONES':
            return 'phone';
        case 'AIRCALL':
            return 'Aircall';
        case 'DIALPAD':
            return 'Dialpad';
        case 'CALLRAIL':
            return 'CallRail';
        case 'RINGCENTRAL':
            return 'RingCentral';
        case 'CLICKTOCALL':
            return 'click-to-call';
        case 'KIXIE':
            return 'Kixie';
        case 'SKYPE':
            return 'Skype';
        default:
            return 'Virtual Meeting';
    }
};

export const getServiceSupportUrl = (
    serviceType: ExternalServiceType | ExternalServiceTypeFrontend
): string => {
    switch (serviceType) {
        case 'ZOOM':
            return 'https://support.zoom.us/hc/en-us/articles/203741855-Starting-a-cloud-recording';
        case 'GOOGLE_MEET':
            return 'https://support.google.com/meet/answer/9308681?hl=en&sjid=9392476484108956679-NA';
        case 'MICROSOFT_TEAMS':
            return 'https://support.microsoft.com/en-us/office/record-a-meeting-in-microsoft-teams-34dfbe7f-b07d-4a27-b4c6-de62f1348c24';
        case 'RINGCENTRAL':
            return 'https://www.nutshell.com/integrations/ringcentral';
        case 'CALLRAIL':
            return 'https://www.nutshell.com/integrations/callrail';
        case 'DIALPAD':
            return 'https://www.nutshell.com/integrations/dialpad';
        case 'KIXIE':
            return 'https://www.nutshell.com/integrations/kixie';
        case 'AIRCALL':
        case 'PHONES':
            return 'https://www.nutshell.com/blog/additional-crm-phone-integrations';
        default:
            return '';
    }
};

export const getServiceUrl = (serviceType: ExternalServiceType): string => {
    switch (serviceType) {
        case 'ZOOM':
            return '/sales/meetings/zoom';
        case 'GOOGLE_MEET':
            return '/sales/meetings/meet';
        case 'MICROSOFT_TEAMS':
            return '/sales/meetings/teams';
        default:
            return '/sales/meetings';
    }
};

export const getLogModes = (
    serviceType: ExternalServiceType
): {
    label: string;
    sublabel: string | null | undefined;
    value: LogMode;
    tooltipInfo?: string;
}[] => {
    return [
        {
            label: 'Manually – I’ll log them when I’m ready',
            sublabel: null,
            value: LOG_MODE_MANUAL,
        },
        {
            label: `Automatically – Log & summarize my recorded ${getServiceNoun(
                serviceType
            )} calls`,
            sublabel: NutClientConfig.hasActivitySyncContactCreationFF
                ? `Will create new contacts for participants that aren’t already in Nutshell`
                : null,
            value: LOG_MODE_AUTOLOG,
            tooltipInfo:
                'Calls will be automatically logged without a transcription and summary if no AI outcomes are available',
        },
    ];
};

export const getIntegrationIsEnabled = (
    serviceType: ExternalServiceType,
    user?: User | null
): boolean => {
    if (!user) {
        return false;
    }

    switch (serviceType) {
        case 'ZOOM':
            return user.zoomIntegration.isEnabled;
        case 'GOOGLE_MEET':
            return user.googleMeetIntegration.isEnabledMeetSync;
        case 'MICROSOFT_TEAMS':
            return user.microsoftTeamsIntegration.isEnabled;
        default:
            return false;
    }
};

export const getServiceAutoLogSettingName = (
    serviceType: ExternalServiceType
): string | null | undefined => {
    switch (serviceType) {
        case 'ZOOM':
            return Nut_Settings_User.SETTING_HAS_ENABLED_ZOOM_AUTOLOG;
        case 'GOOGLE_MEET':
            return Nut_Settings_User.SETTING_HAS_ENABLED_GOOGLE_MEET_AUTOLOG;
        case 'MICROSOFT_TEAMS':
            return Nut_Settings_User.SETTING_HAS_ENABLED_MICROSOFT_TEAMS_AUTOLOG;
        case 'AIRCALL':
            return Nut_Settings_User.SETTING_HAS_ENABLED_AIRCALL_AUTOLOG;
        case 'CALLRAIL':
            return Nut_Settings_User.SETTING_HAS_ENABLED_CALLRAIL_AUTOLOG;
        case 'RINGCENTRAL':
            return Nut_Settings_User.SETTING_HAS_ENABLED_RINGCENTRAL_AUTOLOG;
        case 'DIALPAD':
            return Nut_Settings_User.SETTING_HAS_ENABLED_DIALPAD_AUTOLOG;
        case 'KIXIE':
            return Nut_Settings_User.SETTING_HAS_ENABLED_KIXIE_AUTOLOG;
        default:
            return null;
    }
};

export const getUserAutoLogSettingName = (
    serviceType: ExternalServiceType,
    user?: User | null
): string | null | undefined => {
    if (!user) {
        return null;
    }

    switch (serviceType) {
        case 'ZOOM':
            return USER_SETTING_HAS_ENABLED_ZOOM_AUTOLOG;
        case 'GOOGLE_MEET':
            return USER_SETTING_HAS_ENABLED_GOOGLE_MEET_AUTOLOG;
        case 'MICROSOFT_TEAMS':
            return USER_SETTING_HAS_ENABLED_MICROSOFT_TEAMS_AUTOLOG;
        case 'AIRCALL':
            return USER_SETTING_HAS_ENABLED_AIRCALL_AUTOLOG;
        case 'CALLRAIL':
            return USER_SETTING_HAS_ENABLED_CALLRAIL_AUTOLOG;
        case 'RINGCENTRAL':
            return USER_SETTING_HAS_ENABLED_RINGCENTRAL_AUTOLOG;
        case 'DIALPAD':
            return USER_SETTING_HAS_ENABLED_DIALPAD_AUTOLOG;
        case 'KIXIE':
            return USER_SETTING_HAS_ENABLED_KIXIE_AUTOLOG;
        default:
            return null;
    }
};

export const isUserAutoLogEnabled = (
    serviceType: ExternalServiceType,
    user?: User | null
): boolean => {
    if (!user) {
        return false;
    }

    switch (serviceType) {
        case 'ZOOM':
            return user.hasEnabledZoomAutolog;
        case 'GOOGLE_MEET':
            return user.hasEnabledGoogleMeetAutolog;
        case 'MICROSOFT_TEAMS':
            return user.hasEnabledMicrosoftTeamsAutolog;
        case 'AIRCALL':
            return user.hasEnabledAircallAutolog;
        case 'CALLRAIL':
            return user.hasEnabledCallRailAutolog;
        case 'RINGCENTRAL':
            return user.hasEnabledRingCentralAutolog;
        case 'DIALPAD':
            return user.hasEnabledDialpadAutolog;
        case 'KIXIE':
            return user.hasEnabledKixieAutolog;
        default:
            return false;
    }
};

export function getStatusMessage(isEnabled: boolean, serviceType: ExternalServiceType) {
    if (serviceType === 'GOOGLE_MEET') {
        return isEnabled ? 'Google Meet is turned on' : 'Google Meet is turned off';
    } else if (serviceType === 'MICROSOFT_TEAMS') {
        return isEnabled ? 'Microsoft Teams is turned on' : 'Microsoft Teams is turned off';
    }

    return undefined;
}

export function getFlashBannerMessage(monthlyUsage: MonthlyUsage) {
    if (monthlyUsage.remaining <= 0) {
        return 'Automatically logging summaries and transcriptions unavailable. Additional credits required';
    }

    if (monthlyUsage.remaining < 10) {
        return 'Automatically logging summaries and transcriptions will be disabled soon. Additional credits required';
    }

    return <></>;
}

export function getRecordingRequiredMessage(serviceType: ExternalServiceType) {
    let link = '';
    let planName = '';
    switch (serviceType) {
        case 'GOOGLE_MEET':
            link = 'https://support.google.com/meet/answer/9308681?hl=en#zippy=';
            planName = 'Google Workspace';
            break;

        case 'ZOOM':
            link = 'https://support.zoom.com/hc/en/article?id=zm_kb&sysparm_article=KB0062627';
            planName = 'Zoom';
            break;
        case 'MICROSOFT_TEAMS':
            link =
                'https://support.microsoft.com/en-us/office/record-a-meeting-in-microsoft-teams-34dfbe7f-b07d-4a27-b4c6-de62f1348c24';
            planName = 'Microsoft Teams';
            break;
        default:
            return undefined;
    }

    return (
        <Body size='small'>
            Your {planName} plan must support cloud recording to utilize Nutshell transcription and
            summarization.{' '}
            <Link variant='primary' newTab={true} href={link} size='small'>
                Learn more
            </Link>
        </Body>
    );
}

export const getUnloggedMeetingsSubtitle = (
    serviceType: ExternalServiceType | ExternalServiceTypeFrontend,
    hasLoggedMeetings: boolean
): string => {
    const serviceNoun = getServiceNoun(serviceType);

    const meetingsSubtitle = hasLoggedMeetings
        ? `${serviceNoun} meetings that are not automatically logged as activities will appear here`
        : `To begin tracking ${serviceNoun} meetings with recordings and transcripts, please ensure your ${serviceNoun} account is configured as follows:`;

    const callsSubtitle = hasLoggedMeetings
        ? `Calls that are not automatically logged as activities will appear here`
        : `To begin tracking calls with recordings and transcripts, please ensure your connected integration(s) are configured as follows:`;

    return serviceType === 'PHONES' ? callsSubtitle : meetingsSubtitle;
};

export const getControlBarSubheader = (
    serviceType: ExternalServiceType | ExternalServiceTypeFrontend,
    hasLoggedMeetings: boolean,
    hasUnloggedMeetings: boolean,
    activeTab: ActiveTab
): string => {
    const serviceNoun = getServiceNoun(serviceType);
    switch (activeTab) {
        case TAB_UNLOGGED:
            return hasLoggedMeetings
                ? `Your unlogged meetings will appear here`
                : `Ensure ${serviceNoun} permissions are configured to sync your first meeting`;
        case TAB_LOGGED:
            return hasUnloggedMeetings
                ? `Your logged meetings will appear here`
                : `Ensure ${serviceNoun} permissions are configured to sync your first meeting`;
        default:
            return '';
    }
};

export const getControlBarSubheaderForPhones = (
    hasLoggedMeetings: boolean,
    hasUnloggedMeetings: boolean,
    activeTab: ActiveTab,
    hasAnyIntegrationEnabled: boolean
): string => {
    if (!hasAnyIntegrationEnabled) {
        return 'Once you’re connected, calls will sync into Nutshell and appear here';
    }

    switch (activeTab) {
        case TAB_UNLOGGED:
            return 'Your calls will appear here as unlogged activities';
        case TAB_LOGGED:
            return 'Your calls will appear here as logged activities';
        default:
            return '';
    }
};

export const getServiceNounPlural = (
    serviceType: ExternalServiceType | ExternalServiceTypeFrontend
): string => {
    switch (serviceType) {
        case 'PHONES':
            return 'calls';
        default:
            return 'meetings';
    }
};

export const getServiceNounSingular = (
    serviceType: ExternalServiceType | ExternalServiceTypeFrontend
): string => {
    switch (serviceType) {
        case 'PHONES':
            return 'call';
        default:
            return 'meeting';
    }
};
